import React, { useState, forwardRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaMapMarkerAlt, FaArrowRight } from 'react-icons/fa';
import { getImageUrl } from '../utils/api';
import { formatDisplayPrice } from '../utils/formatPrice';
import { getShareExtent } from '../utils/formatMeasurement';

const HighlightCard = forwardRef(({ property }, ref) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [copiedToast, setCopiedToast]       = useState(false);
  const [sharePanelOpen, setSharePanelOpen] = useState(false);

  // Share URL always points to the production backend OG proxy — never localhost.
  const getShareUrl = () => {
    const PRODUCTION_BACKEND = 'https://api.tanaviproperties.com';
    const configured = process.env.REACT_APP_API_URL || '';
    const isLocal = /localhost|127\.0\.0\.1/.test(configured);
    const base = (isLocal ? PRODUCTION_BACKEND : (configured || PRODUCTION_BACKEND))
      .replace(/\/$/, '')
      .replace(/^http:\/\//i, 'https://');
    return `${base}/api/share/${property._id}`;
  };

  const handleShare = async (e) => {
    e.stopPropagation();

    const shareUrl     = getShareUrl();
    const propertyCode = property.propertyCode ? `[${property.propertyCode}] ` : '';
    const shareTitle   = `${propertyCode}${property.title}`;
    const extentDisplay = getShareExtent(property);
    const extentLine   = extentDisplay ? `\n📐 ${extentDisplay}` : '';
    const shareText    = `${propertyCode}${property.title}\n📍 ${property.location}\n💰 ${formatDisplayPrice(property.price)}\n🏡 ${property.category || 'Property'}${extentLine}\n\n${shareUrl}`;

    const copyToClipboard = async () => {
      try {
        await navigator.clipboard.writeText(shareText);
      } catch (_) {
        try {
          const ta = document.createElement('textarea');
          ta.value = shareText;
          ta.style.position = 'fixed';
          ta.style.opacity  = '0';
          document.body.appendChild(ta);
          ta.select();
          document.execCommand('copy');
          document.body.removeChild(ta);
        } catch (__) {}
      }
      setCopiedToast(true);
      setTimeout(() => setCopiedToast(false), 2500);
    };

    const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
    if (isMobile && navigator.share) {
      try {
        await navigator.share({ title: shareTitle, text: shareText });
      } catch (err) {
        if (err.name !== 'AbortError') await copyToClipboard();
      }
    } else {
      // Desktop: copy full message AND open social share panel
      await copyToClipboard();
      setSharePanelOpen(true);
    }
  };

  // Build social links for the panel
  const buildSocialLinks = () => {
    const shareUrl    = getShareUrl();
    const propertyCode = property.propertyCode ? `[${property.propertyCode}] ` : '';
    const encodedUrl   = encodeURIComponent(shareUrl);
    const encodedTitle = encodeURIComponent(`${propertyCode}${property.title}`);
    const extentDisplay = getShareExtent(property);
    const extentLine   = extentDisplay ? `\n📐 ${extentDisplay}` : '';
    const rawText      = `${propertyCode}${property.title}\n📍 ${property.location}\n💰 ${formatDisplayPrice(property.price)}\n🏡 ${property.category || 'Property'}${extentLine}\n\n${shareUrl}`;
    const encodedText  = encodeURIComponent(rawText);
    const waText = encodeURIComponent(rawText);

    const isMobileDevice = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
    const whatsappHref = isMobileDevice
      ? `whatsapp://send?text=${waText}`
      : `https://web.whatsapp.com/send?text=${waText}`;

    return [
      {
        name: 'WhatsApp',
        color: 'bg-[#25D366] hover:bg-[#1ebe5a]',
        href: whatsappHref,
        icon: (
          <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
            <path d="M12 0C5.373 0 0 5.373 0 12c0 2.135.558 4.136 1.535 5.874L0 24l6.318-1.508A11.952 11.952 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818a9.793 9.793 0 01-5.002-1.372l-.359-.213-3.724.888.927-3.621-.234-.372A9.794 9.794 0 012.182 12c0-5.415 4.403-9.818 9.818-9.818 5.415 0 9.818 4.403 9.818 9.818 0 5.415-4.403 9.818-9.818 9.818z"/>
          </svg>
        ),
      },
      {
        name: 'Facebook',
        color: 'bg-[#1877F2] hover:bg-[#1465d8]',
        href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
        icon: (
          <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current">
            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
          </svg>
        ),
      },
      {
        name: 'Twitter/X',
        color: 'bg-black hover:bg-gray-800',
        href: `https://twitter.com/intent/tweet?text=${encodedText}`,
        icon: (
          <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current">
            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.742l7.737-8.835L1.254 2.25H8.08l4.253 5.622L18.244 2.25zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77z"/>
          </svg>
        ),
      },
      {
        name: 'Telegram',
        color: 'bg-[#229ED9] hover:bg-[#1a8cbf]',
        href: `https://t.me/share/url?url=${encodedUrl}&text=${encodedText}`,
        icon: (
          <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current">
            <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
          </svg>
        ),
      },
      {
        name: 'LinkedIn',
        color: 'bg-[#0A66C2] hover:bg-[#0858a8]',
        href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
        icon: (
          <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current">
            <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
          </svg>
        ),
      },
      {
        name: 'Gmail',
        color: 'bg-[#EA4335] hover:bg-[#d33426]',
        href: `https://mail.google.com/mail/?view=cm&su=${encodedTitle}&body=${encodedText}`,
        icon: (
          <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current">
            <path d="M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 0 1 0 19.366V5.457c0-2.023 2.309-3.178 3.927-1.964L5.455 4.64 12 9.548l6.545-4.91 1.528-1.145C21.69 2.28 24 3.434 24 5.457z"/>
          </svg>
        ),
      },
    ];
  };

  return (
    <>
      {/* Copied toast */}
      {copiedToast && (
        <div className="fixed top-24 left-1/2 -translate-x-1/2 z-50 pointer-events-none">
          <div className="bg-gray-900 text-white text-sm font-medium px-5 py-2.5 rounded-full shadow-xl flex items-center gap-2">
            <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
            Property link copied successfully.
          </div>
        </div>
      )}

      {/* Desktop Social Share Panel */}
      {sharePanelOpen && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm"
          onClick={(e) => { e.stopPropagation(); setSharePanelOpen(false); }}
        >
          <div
            className="bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl w-full sm:max-w-sm mx-auto p-6"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold text-gray-900">Share Property</h3>
              <button
                onClick={() => setSharePanelOpen(false)}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition text-gray-600 font-bold"
              >
                ✕
              </button>
            </div>

            {/* Property preview */}
            <div className="flex items-center gap-3 bg-gray-50 rounded-xl p-3 mb-5 border border-gray-200">
              {property.images?.[0] && (
                <img
                  src={getImageUrl(property.images[0])}
                  alt={property.title}
                  className="w-14 h-14 rounded-lg object-cover flex-shrink-0 border border-gray-200"
                />
              )}
              <div className="min-w-0">
                <p className="text-sm font-bold text-gray-800 truncate">
                  {property.propertyCode ? `[${property.propertyCode}] ` : ''}{property.title}
                </p>
                <p className="text-xs text-gray-500 truncate">{property.category} • {property.location}</p>
                <p className="text-xs font-semibold text-primary">{formatDisplayPrice(property.price)}</p>
              </div>
            </div>

            {/* Social buttons */}
            <div className="grid grid-cols-3 gap-3 mb-5">
              {buildSocialLinks().map(s => (
                <a
                  key={s.name}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`flex flex-col items-center gap-1.5 ${s.color} text-white rounded-xl py-3 px-2 transition font-medium text-xs`}
                >
                  {s.icon}
                  {s.name}
                </a>
              ))}
            </div>

            {/* Copy link row */}
            <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-xl px-4 py-3">
              <span className="text-xs text-gray-500 truncate flex-1">{getShareUrl()}</span>
              <button
                onClick={async () => {
                  const shareUrl     = getShareUrl();
                  const propertyCode = property.propertyCode ? `[${property.propertyCode}] ` : '';
                  const extentDisplay = getShareExtent(property);
                  const extentLine   = extentDisplay ? `\n📐 ${extentDisplay}` : '';
                  const fullText     = `${propertyCode}${property.title}\n📍 ${property.location}\n💰 ${formatDisplayPrice(property.price)}\n🏡 ${property.category || 'Property'}${extentLine}\n\n${shareUrl}`;
                  try { await navigator.clipboard.writeText(fullText); } catch (_) {}
                  setCopiedToast(true);
                  setTimeout(() => setCopiedToast(false), 2500);
                  setSharePanelOpen(false);
                }}
                className="flex-shrink-0 bg-primary text-white text-xs font-semibold px-3 py-1.5 rounded-lg hover:bg-primary/90 transition"
              >
                Copy
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Card */}
      <div ref={ref} className="group relative bg-secondary/10 border-2 border-secondary rounded-xl overflow-hidden shadow-md hover:shadow-2xl hover:border-primary transition-all duration-500 h-full">
        <div className="relative h-64 overflow-hidden">
          <img
            src={getImageUrl(property.images?.[0] || property.image)}
            alt={property.title}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
            loading="lazy"
          />
          {/* Property ID + price header */}
          <div className="absolute inset-x-3 top-3 z-20 flex flex-wrap items-center justify-between gap-2">
            {property.propertyCode && (
              <span className="bg-orange-600 text-white text-xs font-semibold px-2 py-1 rounded shadow-sm whitespace-nowrap">
                ID: {property.propertyCode}
              </span>
            )}

            <span className="bg-white/50 backdrop-blur-sm text-gray-900 px-3 py-1.5 rounded-full text-sm font-bold shadow-sm whitespace-nowrap">
              {formatDisplayPrice(property.price)}
            </span>
          </div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
        </div>

        <div className="p-6">
          <h3 className="text-xl font-bold mb-2 text-textDark group-hover:text-primary transition-colors">{property.title}</h3>
          <div className="flex items-center text-textGray mb-4">
            <FaMapMarkerAlt className="mr-2 text-primary" />
            <span className="text-sm">{property.location}</span>
          </div>

          <div className="flex items-center justify-between gap-2">
            <button
              onClick={() => {
                const propertyId = property._id || property.id;
                try {
                  sessionStorage.setItem('restoreScrollId', propertyId);
                  sessionStorage.setItem('restoreScrollSection', 'highlights');
                } catch (_) {}
                navigate(`/property/${propertyId}`, {
                  state: {
                    fromRoute: location.pathname,
                    clickedPropertyId: propertyId,
                    section: 'highlights',
                  },
                });
              }}
              className="flex items-center gap-2 text-primary font-semibold group-hover:text-secondary transition-colors"
            >
              <span>View Details</span>
              <FaArrowRight className="group-hover:translate-x-2 transition-transform duration-300" />
            </button>

            <button
              onClick={handleShare}
              className="p-2 text-primary hover:bg-primary hover:text-white rounded-full transition"
              title="Share"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="18" cy="5" r="3"/>
                <circle cx="6" cy="12" r="3"/>
                <circle cx="18" cy="19" r="3"/>
                <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
                <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
              </svg>
            </button>
          </div>
        </div>
      </div>
    </>
  );
});

HighlightCard.displayName = 'HighlightCard';

export default HighlightCard;
